<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';

import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import {
  isWorkspaceV2Path,
  workspaceV2Path,
} from 'dashboard/helper/workspaceV2';
import { worldById } from 'dashboard/routes/dashboard/hub/hubWorldLookup';

import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import SearchResultConversationItem from 'dashboard/modules/search/components/SearchResultConversationItem.vue';
import SearchResultContactItem from 'dashboard/modules/search/components/SearchResultContactItem.vue';
import SearchResultMessageItem from 'dashboard/modules/search/components/SearchResultMessageItem.vue';
import MessageContent from 'dashboard/modules/search/components/MessageContent.vue';

const props = defineProps({
  searchQuery: { type: String, default: '' },
  // Gate extra do ancora (ex.: o campo de busca do header so esta "aberto"
  // quando focado ou com texto). Por padrao sempre habilitado.
  anchorOpen: { type: Boolean, default: true },
});

const emit = defineEmits(['close']);

// SiteUp: popup de busca global ancorado na busca da lista de conversas.
// Dispara so as 3 categorias que o popup exibe (conversas, contatos,
// mensagens). fullSearch fica pra SearchView.vue (tela cheia), que tambem
// precisa de artigos e kanban_items.
const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 350;
const RESULTS_PER_SECTION = 5;

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const { accountScopedRoute } = useAccount();

const accountId = useMapGetter('getCurrentAccountId');
const uiFlags = useMapGetter('conversationSearch/getUIFlags');
const contactRecords = useMapGetter('conversationSearch/getContactRecords');
const conversationRecords = useMapGetter(
  'conversationSearch/getConversationRecords'
);
const messageRecords = useMapGetter('conversationSearch/getMessageRecords');

const trimmedQuery = computed(() => (props.searchQuery || '').trim());
const hasMinLength = computed(
  () => trimmedQuery.value.length >= MIN_QUERY_LENGTH
);
const fromWorldId = computed(() => {
  const candidate = route.query?.from_world;
  return typeof candidate === 'string' ? worldById(candidate)?.id : undefined;
});

// Fechado manualmente (Escape, clique fora, clique num resultado). Volta a
// false assim que o texto muda de novo, pra reabrir naturalmente ao digitar.
const manuallyClosed = ref(false);

// SiteUp: estado local de busca. Sem fullSearch, isFetching/isSearchCompleted
// do Vuex nao sao setados; o empty state depende desses refs locais.
const isLocalFetching = ref(false);
const isLocalSearchCompleted = ref(false);
const inFlightQuery = ref('');

const isOpen = computed(
  () => props.anchorOpen && hasMinLength.value && !manuallyClosed.value
);

const close = () => {
  if (manuallyClosed.value) return;
  manuallyClosed.value = true;
  emit('close');
};

// Mesmo transform que a SearchView.vue aplica antes de passar os registros
// pros componentes de item (os getters trazem snake_case do backend).
const toCamelCaseList = records =>
  (records || []).map(record => useCamelCase(record, { deep: true }));

// SiteUp: corta ANTES do camelCase deep. Transformar a pagina inteira so
// pra mostrar 5 itens trava a digitacao na conta grande.
const contacts = computed(() =>
  toCamelCaseList((contactRecords.value || []).slice(0, RESULTS_PER_SECTION))
);
const conversations = computed(() =>
  toCamelCaseList(
    (conversationRecords.value || []).slice(0, RESULTS_PER_SECTION)
  ).map(conversation => ({
    ...conversation,
    mailSubject: conversation.additionalAttributes?.mailSubject || '',
  }))
);
const messages = computed(() =>
  toCamelCaseList((messageRecords.value || []).slice(0, RESULTS_PER_SECTION))
);

const getMessageAuthor = message =>
  message?.sender?.name ? message.sender.name : t('SEARCH.BOT_LABEL');

const totalResultsCount = computed(
  () =>
    contacts.value.length + conversations.value.length + messages.value.length
);

const isFetchingAny = computed(() => {
  const flags = uiFlags.value || {};
  return Boolean(
    isLocalFetching.value ||
      flags.contact?.isFetching ||
      flags.conversation?.isFetching ||
      flags.message?.isFetching
  );
});

const showLoading = computed(
  () => isFetchingAny.value && totalResultsCount.value === 0
);
const showEmptyState = computed(
  () =>
    hasMinLength.value &&
    isLocalSearchCompleted.value &&
    !isFetchingAny.value &&
    totalResultsCount.value === 0
);

const viewAllRoute = computed(() => ({
  ...(isWorkspaceV2Path(route.path)
    ? {
        path: workspaceV2Path(`/app/accounts/${route.params.accountId}/search`),
      }
    : accountScopedRoute('search')),
  query: {
    q: trimmedQuery.value,
    ...(isWorkspaceV2Path(route.path) && fromWorldId.value
      ? { from_world: fromWorldId.value }
      : {}),
  },
}));

const resetLocalSearchState = () => {
  isLocalFetching.value = false;
  isLocalSearchCompleted.value = false;
  inFlightQuery.value = '';
};

// Sempre le o valor ATUAL da prop na hora de disparar: se o texto ja mudou
// (ficou curto de novo, ou mudou pra outra busca) entre o agendamento e o
// disparo do debounce, evita mandar uma query velha pro backend.
const runSearch = async () => {
  const query = trimmedQuery.value;
  if (query.length < MIN_QUERY_LENGTH) return;

  // As actions de categoria acumulam (append) nos getters; limpa antes.
  store.dispatch('conversationSearch/clearSearchResults');
  inFlightQuery.value = query;
  isLocalFetching.value = true;
  isLocalSearchCompleted.value = false;

  try {
    await Promise.all([
      store.dispatch('conversationSearch/contactSearch', { q: query }),
      store.dispatch('conversationSearch/conversationSearch', { q: query }),
      store.dispatch('conversationSearch/messageSearch', { q: query }),
    ]);
  } finally {
    // SiteUp: resposta velha nao marca "concluido" pra query nova.
    if (inFlightQuery.value === query && trimmedQuery.value === query) {
      isLocalFetching.value = false;
      isLocalSearchCompleted.value = true;
    }
  }
};

const debouncedSearch = debounce(runSearch, SEARCH_DEBOUNCE_MS, false);

watch(
  () => props.searchQuery,
  () => {
    manuallyClosed.value = false;
    if (!hasMinLength.value) {
      store.dispatch('conversationSearch/clearSearchResults');
      resetLocalSearchState();
      return;
    }
    debouncedSearch();
  }
);

const handleKeydown = event => {
  if (event.key === 'Escape' && isOpen.value) {
    close();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleKeydown);
});
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});

// Clique fora fecha, mas clicar de volta no proprio campo de busca (que fica
// FORA do painel, e o mesmo testid nos dois modos do header) nao conta como
// "fora": e so o atendente voltando a digitar/mover o cursor.
const handleClickAway = event => {
  if (
    event.target?.closest?.('[data-testid="conversation-list-search-input"]')
  ) {
    return;
  }
  close();
};

// Clicar num resultado (ou no link "ver todos") navega e fecha o popup: os
// itens reusados sao <router-link>, entao delega pro <a> mais proximo.
const handlePanelClick = event => {
  if (event.target?.closest?.('a')) {
    close();
  }
};
</script>

<!-- eslint-disable vue/no-root-v-if -->
<template>
  <div
    v-if="isOpen"
    v-on-clickaway="handleClickAway"
    data-testid="chat-list-search-overlay"
    role="dialog"
    :aria-label="t('CHAT_LIST.SEARCH.INPUT')"
    class="absolute inset-x-0 top-[calc(100%+0.375rem)] z-50 max-h-[70vh] min-w-0 overflow-y-auto rounded-xl border border-n-weak bg-n-solid-1 shadow-lg"
    @click="handlePanelClick"
  >
    <div v-if="showLoading" class="p-2">
      <LoadingState :message="t('CHAT_LIST.SEARCH.GLOBAL_SEARCH.SEARCHING')" />
    </div>

    <div
      v-else-if="showEmptyState"
      data-testid="chat-list-search-empty"
      class="flex flex-col items-center justify-center gap-2 px-4 py-6 text-center"
    >
      <span
        class="i-ph-magnifying-glass size-5 shrink-0 text-n-slate-10"
        aria-hidden="true"
      />
      <p class="m-0 text-sm text-n-slate-11">
        {{
          t('CHAT_LIST.SEARCH.GLOBAL_SEARCH.EMPTY_STATE', {
            query: trimmedQuery,
          })
        }}
      </p>
    </div>

    <div v-else class="min-w-0 p-2">
      <section v-if="conversations.length" class="mb-2 min-w-0">
        <h3
          class="m-0 truncate px-2 py-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('SEARCH.SECTION.CONVERSATIONS') }}
        </h3>
        <ul class="m-0 list-none space-y-1 p-0">
          <li
            v-for="conversation in conversations"
            :key="`conversation-${conversation.id}`"
            class="min-w-0"
          >
            <SearchResultConversationItem
              :id="conversation.id"
              :name="conversation.contact?.name"
              :email="conversation.contact?.email"
              :account-id="accountId"
              :inbox="conversation.inbox"
              :created-at="conversation.createdAt"
              :email-subject="conversation.mailSubject"
            />
          </li>
        </ul>
      </section>

      <section v-if="contacts.length" class="mb-2 min-w-0">
        <h3
          class="m-0 truncate px-2 py-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('SEARCH.SECTION.CONTACTS') }}
        </h3>
        <ul class="m-0 list-none space-y-1 p-0">
          <li
            v-for="contact in contacts"
            :key="`contact-${contact.id}`"
            class="min-w-0"
          >
            <SearchResultContactItem
              :id="contact.id"
              :name="contact.name"
              :email="contact.email"
              :phone="contact.phoneNumber"
              :additional-attributes="contact.additionalAttributes"
              :account-id="accountId"
              :thumbnail="contact.thumbnail"
              :updated-at="contact.lastActivityAt"
            />
          </li>
        </ul>
      </section>

      <section v-if="messages.length" class="min-w-0">
        <h3
          class="m-0 truncate px-2 py-1 text-xs font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('SEARCH.SECTION.MESSAGES') }}
        </h3>
        <ul class="m-0 list-none space-y-1 p-0">
          <li
            v-for="message in messages"
            :key="`message-${message.id}`"
            class="min-w-0"
          >
            <SearchResultMessageItem
              :id="message.conversationId"
              :account-id="accountId"
              :inbox-id="message.inboxId"
              :created-at="message.createdAt"
              :message-id="message.id"
              :is-private="message.private"
              :attachments="message.attachments"
            >
              <MessageContent
                :author="getMessageAuthor(message)"
                :message="message"
                :search-term="trimmedQuery"
              />
            </SearchResultMessageItem>
          </li>
        </ul>
      </section>
    </div>

    <router-link
      v-if="hasMinLength"
      :to="viewAllRoute"
      data-testid="chat-list-search-view-all"
      class="block truncate rounded-b-xl border-t border-n-weak px-3 py-2.5 text-center text-sm font-medium text-n-blue-11 hover:bg-n-alpha-2"
    >
      {{ t('CHAT_LIST.SEARCH.GLOBAL_SEARCH.VIEW_ALL_RESULTS') }}
    </router-link>
  </div>
</template>
